import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {useState} from 'react'
import {TreatmentRequestFollowUpForm} from '@/features/treatment-requests/components/treatment-request-follow-up-form/TreatmentRequestFollowUpForm'
import {StyledAddFollowUpButton} from './style'
import {useCreateFollowUp} from '@/features/treatment-requests/services/useCreateFollowUp'
import {errorHandler} from '@/utilities/helpers'
import {formatDate} from '@/dayjs'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {OnSubmitFollowUp, TreatmentRequestExtended} from '@/features/treatment-requests/types'
import {useOutletContext} from 'react-router-dom'
import {Text} from '@/components/ui/text/Text'

export const TreatmentRequestNewFollowUp = ({
    onToggleNewFollowUp,
    buttonPosition
}: {
    onToggleNewFollowUp?: (value: boolean) => void
    buttonPosition?: 'start' | 'end'
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const {
        treatmentRequest: {id}
    } = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()

    const handleToggleOpenFollowUp = () => {
        setIsOpen(prev => !prev)
        onToggleNewFollowUp?.(!isOpen)
    }

    const createFollowUpMutation = useCreateFollowUp({
        onSuccess: () => {
            handleToggleOpenFollowUp()
            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, id]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, id]})
        },
        onError: errorHandler
    })

    const handleCreateFollowUp = (values: OnSubmitFollowUp<undefined>) => {
        createFollowUpMutation.mutate({
            payload: {
                treatmentRequestId: id,
                date: formatDate(values.date.toISOString(), 'YYYY-MM-DD'),
                notes: values.notes ?? undefined
            }
        })
    }

    if (!isOpen) {
        return (
            <StyledAddFollowUpButton
                variant="primaryNeutral"
                $position={buttonPosition}
                onClick={handleToggleOpenFollowUp}
                aria-label={t('treatment_requests:add_follow_up')}
            >
                <PlusIcon />
                {t('treatment_requests:follow_up', {count: 1})}
            </StyledAddFollowUpButton>
        )
    }

    return (
        <Flexbox direction="column" gap={3} fullWidth>
            <Text size="textSm">{t('treatment_requests:add_follow_up')}</Text>
            <TreatmentRequestFollowUpForm
                isLoading={createFollowUpMutation.isPending}
                onSubmit={value => handleCreateFollowUp(value)}
                onCancel={handleToggleOpenFollowUp}
            />
        </Flexbox>
    )
}
